import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { UnionProvider, useUnion } from './contexts/UnionContext';
import { ApartmentProvider } from './contexts/ApartmentContext';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Register from './pages/Register';
import Apartments from './pages/Apartments';
import ApartmentForm from './pages/ApartmentForm';
import UnionsAdmin from './pages/admin/UnionsAdmin';
import UtilityTypesAdmin from './pages/admin/UtilityTypesAdmin';
import UnionMembersAdmin from './pages/admin/UnionMembersAdmin';
import Profile from './pages/Profile';
import AcceptInvitation from './pages/AcceptInvitation';
import SentInvoices from './pages/SentInvoices';
import MeterReadings from './pages/MeterReadings';
import Transactions from './pages/Transactions';
import BillingPeriods from './pages/BillingPeriods';
import Forum from './pages/Forum';
import FileManager from './pages/admin/FileManager';
import ForumTopic from './pages/ForumTopic';
import Onboarding from './pages/Onboarding';
import PrivateRoute from './components/PrivateRoute';
import authService from './services/authService';

function LanguageSync() {
  const { i18n } = useTranslation();
  useEffect(() => {
    if (!authService.isAuthenticated()) return;
    authService.getUserInfo().then(data => {
      if (data.language && data.language !== i18n.language) {
        i18n.changeLanguage(data.language);
      }
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

function OnboardingGate({ children }) {
  const { unions, loading } = useUnion();
  const [dismissed, setDismissed] = useState(false);
  const [started, setStarted] = useState(false);
  const navigate = useNavigate();

  const isAuth = authService.isAuthenticated();
  const needsOnboarding = isAuth && !loading && unions.length === 0;
  const showOnboarding = (needsOnboarding || started) && !dismissed;

  if (needsOnboarding && !started) setStarted(true);

  if (showOnboarding) {
    return (
      <>
        {children}
        <Onboarding onComplete={() => { setDismissed(true); navigate('/dashboard'); }} />
      </>
    );
  }
  return children;
}

function App() {
  return (
    <UnionProvider>
      <ApartmentProvider>
      <LanguageSync />
      <OnboardingGate>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/invitation/:token" element={<AcceptInvitation />} />
        <Route
          path="/dashboard"
          element={
            <PrivateRoute>
              <Dashboard />
            </PrivateRoute>
          }
        />
        <Route
          path="/apartments"
          element={
            <PrivateRoute>
              <Apartments />
            </PrivateRoute>
          }
        />
        <Route
          path="/apartments/new"
          element={
            <PrivateRoute>
              <ApartmentForm />
            </PrivateRoute>
          }
        />
        <Route
          path="/apartments/:id"
          element={
            <PrivateRoute>
              <ApartmentForm readOnly />
            </PrivateRoute>
          }
        />
        <Route
          path="/apartments/:id/edit"
          element={
            <PrivateRoute>
              <ApartmentForm />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/unions"
          element={
            <PrivateRoute>
              <UnionsAdmin />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/utility-types"
          element={
            <PrivateRoute>
              <UtilityTypesAdmin />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/members"
          element={
            <PrivateRoute>
              <UnionMembersAdmin />
            </PrivateRoute>
          }
        />
        <Route
          path="/sent-invoices"
          element={
            <PrivateRoute>
              <SentInvoices />
            </PrivateRoute>
          }
        />
        <Route
          path="/meter-readings"
          element={
            <PrivateRoute>
              <MeterReadings />
            </PrivateRoute>
          }
        />
        <Route
          path="/transactions"
          element={
            <PrivateRoute>
              <Transactions />
            </PrivateRoute>
          }
        />
        <Route
          path="/billing-periods"
          element={
            <PrivateRoute>
              <BillingPeriods />
            </PrivateRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <PrivateRoute>
              <Profile />
            </PrivateRoute>
          }
        />
        <Route path="/admin/files" element={<PrivateRoute><FileManager /></PrivateRoute>} />
        <Route path="/forum" element={<PrivateRoute><Forum /></PrivateRoute>} />
        <Route path="/forum/:id" element={<PrivateRoute><ForumTopic /></PrivateRoute>} />
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
      </Routes>
      </OnboardingGate>
      </ApartmentProvider>
    </UnionProvider>
  );
}

export default App;
